import { createFileRoute } from "@tanstack/react-router";

import { listBlogCategories, listContent } from "@/lib/cms.functions";
import { normalizeLocale } from "@/lib/i18n";
import { pageHead } from "@/content/seo-pages";
import { PageHero } from "@/components/site/ContentViews";
import {
  ContentHubSkeleton,
  FilteredContentGrid,
} from "@/components/site/ContentFilters";
import { Section } from "@/components/site/Section";
import { ClosingCta } from "@/components/site/Cta";
import { FaqSection } from "@/components/site/FaqSection";
import { BLOG_FAQ } from "@/content/industry-faq";
import { faqScript } from "@/lib/faq-schema";
import { translate } from "@/lib/i18n-dict";

export const Route = createFileRoute("/{-$locale}/blog/")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const [items, categories] = await Promise.all([
      listContent({ data: { kind: "blog", locale, limit: 80 } }),
      listBlogCategories({ data: { locale } }).catch(() => []),
    ]);
    return { locale, items, categories };
  },
  head: ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const head = pageHead("/blog", locale, {
      title: "Blog — manufacturing planning insights | factory.online",
      description:
        "Practical articles on production planning, scheduling, inventory and MRP for small and mid-sized manufacturers.",
    });
    return { ...head, scripts: faqScript(BLOG_FAQ, locale) };
  },
  component: BlogIndex,
  pendingComponent: ContentHubSkeleton,
});

function BlogIndex() {
  const { items, locale, categories } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow={translate(locale, "Blog")}
        title={translate(locale, "Fresh ideas and best practices for your production")}
        lead={translate(locale, "Inspiration and proven practices on planning, inventory and cost control from the factory.online team — you're not in this alone.")}
      />
      <Section>
        <FilteredContentGrid
          items={items}
          categories={categories}
          base="/blog"
          locale={locale}
          emptyLabel={translate(locale, "No articles published yet.")}
          searchPlaceholder={translate(locale, "Search articles…")}
        />
      </Section>
      <FaqSection faqs={BLOG_FAQ} title={translate(locale, "Blog questions, answered")} />
      <ClosingCta />
    </>
  );
}
